/* eslint-disable max-len */
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { type ApplicationMarketTypeFragmentFragment } from '../../../../api/fragments/ApplicationMarketTypeFragment';
import { type DealerWithPermissionsFragmentFragment } from '../../../../api/fragments/DealerWithPermissionsFragment';
import { type StandardApplicationModuleInDealerSpecsFragment } from '../../../../api/fragments/StandardApplicationModuleInDealerSpecs';
import { useUpdateApplicationDealershipAssignmentsByDealerMutation } from '../../../../api/mutations/updateApplicationDealershipAssignmentsByDealer';
import { type TranslatedString } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import hasPermissions from '../../../../utilities/hasPermissions';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import VehicleAssignmentsSection from '../../../shared/VehicleAssignmentsSection';
import DealerMainDetailsSection from './DealerMainDetailsSection';
import FinanceProductAssignmentSection from './FinanceProductAssignmentSection';
import InsuranceProductAssignmentSection from './InsuranceProductAssignmentSection';
import {
    type DealerMarketType,
    dealerMarketTypeValidations,
    getInitDealerMarketType,
    pickDealerMarketType,
} from './MarketTypeEditableField';

export type DealerAssignmentStandardFormProps = {
    module: StandardApplicationModuleInDealerSpecsFragment;
    dealer: DealerWithPermissionsFragmentFragment;
};

type FormValues = {
    dealerMarketType: DealerMarketType;
    dealerPriceDisclaimer: { priceDisclaimer: TranslatedString }[];
    dealerVehicleSuiteIds: string[];
    dealerFinanceProductSuiteIds: string[];
    dealerInsuranceProductSuiteIds: string[];
};

const createValidations = dealerMarketTypeValidations;

const DealerAssignmentStandardForm = ({ module, dealer }: DealerAssignmentStandardFormProps) => {
    if (module.__typename !== 'StandardApplicationModule') {
        return null;
    }
    const { t } = useTranslation(['dealershipManagement']);
    const [mutation] = useUpdateApplicationDealershipAssignmentsByDealerMutation();
    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    const marketType = module.marketType as ApplicationMarketTypeFragmentFragment;

    // initialize finance products and dealers for the dealer
    const initialValues: FormValues = useMemo(
        () => ({
            dealerFinanceProductSuiteIds:
                module.dealerFinanceProducts.find(({ dealerId }) => dealerId === dealer.id)?.financeProductSuiteIds ||
                [],
            dealerInsuranceProductSuiteIds:
                module.dealerInsuranceProducts.find(({ dealerId }) => dealerId === dealer.id)
                    ?.insuranceProductSuiteIds || [],
            dealerPriceDisclaimer: module.dealerPriceDisclaimer.overrides.find(({ dealerId }) => dealerId === dealer.id)
                ?.value
                ? [
                      {
                          priceDisclaimer: module.dealerPriceDisclaimer.overrides.find(
                              ({ dealerId }) => dealerId === dealer.id
                          ).value,
                      },
                  ]
                : [{ priceDisclaimer: module.dealerPriceDisclaimer.defaultValue }],
            dealerVehicleSuiteIds:
                module.dealerVehicles.find(({ dealerId }) => dealerId === dealer.id)?.vehicleSuiteIds || [],
            dealerMarketType: getInitDealerMarketType(marketType, dealer.id),
        }),
        [dealer.id, module, marketType]
    );

    const validator = useMemo(() => createValidations(marketType), [marketType]);
    const validate = useValidator(validator);

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const {
                dealerMarketType,
                dealerPriceDisclaimer,
                dealerFinanceProductSuiteIds,
                dealerVehicleSuiteIds,
                dealerInsuranceProductSuiteIds,
            } = values;

            // submitting message
            message.loading({
                content: t('dealershipManagement:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const dealerVehicles = { dealerId: dealer.id, vehicleSuiteIds: dealerVehicleSuiteIds };
            const dealerFinanceProducts = { dealerId: dealer.id, financeProductSuiteIds: dealerFinanceProductSuiteIds };
            const dealerInsuranceProducts = {
                dealerId: dealer.id,
                insuranceProductSuiteIds: dealerInsuranceProductSuiteIds,
            };

            const variables = {
                moduleId: module.id,
                dealerPriceDisclaimer: {
                    dealerId: dealer.id,
                    value: dealerPriceDisclaimer[0]
                        ? [dealerPriceDisclaimer[0].priceDisclaimer]
                        : [{ defaultValue: '', overrides: [] }],
                },
                dealerVehicles,
                dealerFinanceProducts,
                dealerInsuranceProducts,
                dealerMarketType: {
                    dealerId: dealer.id,
                    ...pickDealerMarketType(marketType, dealerMarketType),
                },
            };

            await mutation({ variables }).finally(() => {
                message.destroy('primary');
            });

            // show successful message
            message.success({
                content: t('dealershipManagement:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [t, dealer.id, module.id, marketType, mutation]
    );

    const formId = `dealerForm-${module.id}`;

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate} enableReinitialize>
            {({ handleSubmit, values }) => (
                <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                    <DealerMainDetailsSection disabled={!hasUpdatePermission} marketType={marketType} module={module} />
                    <VehicleAssignmentsSection
                        filter={{ moduleId: module.vehicleModuleId }}
                        header={t('dealershipManagement:tabs.panel.vehicleAssignment')}
                        name="dealerVehicleSuiteIds"
                        transferFieldProps={{ disabled: !hasUpdatePermission }}
                    />
                    <FinanceProductAssignmentSection
                        disabled={!hasUpdatePermission}
                        moduleId={module.bankModuleId}
                        variantSuiteIds={values.dealerVehicleSuiteIds}
                    />
                    {module.insuranceModuleId && (
                        <InsuranceProductAssignmentSection
                            disabled={!hasUpdatePermission}
                            moduleId={module.insuranceModuleId}
                            variantSuiteIds={values.dealerVehicleSuiteIds}
                        />
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default DealerAssignmentStandardForm;
